// Turns raw encrypted metric readings (as returned by /v1/health/metrics) into the same
// Trend shape the curated demo trends use, so the existing chart components (BigChart, Sparkline,
// DetailModal) render real data with zero changes. A metric only becomes a chart once it has ≥2
// distinct days of history — before that it's shown as a plain snapshot tile.

export type LiveMetric = { type: string; value: number; unit: string; source: string; recorded_at: string }
export type LiveTrend = {
  type: string // raw metric key (e.g. "hrv") — for matching back to the source reading
  metric: string; unit: string; source: string; series: number[]
  dir: 'up' | 'down'; concern: boolean; normal: number; ref: string; note: string
  labels: string[]
}

// Per-metric-type clinical framing: which direction is "good", a rough reference line/range, and
// the label shown in the modal. `higherIsBetter: null` = no inherent direction (e.g. weight) —
// shown without concern coloring.
const META: Record<string, { label: string; higherIsBetter: boolean | null; normal: number; ref: string }> = {
  resting_heart_rate: { label: 'Resting HR', higherIsBetter: false, normal: 70, ref: '60–100 bpm' },
  heart_rate: { label: 'Heart rate', higherIsBetter: false, normal: 80, ref: '60–100 bpm' },
  hrv: { label: 'HRV', higherIsBetter: true, normal: 55, ref: 'Higher is better (typical baseline ~50–60 ms)' },
  recovery_score: { label: 'Recovery', higherIsBetter: true, normal: 67, ref: '67–100% = well recovered' },
  spo2: { label: 'SpO₂', higherIsBetter: true, normal: 95, ref: '≥ 95%' },
  strain: { label: 'Strain', higherIsBetter: null, normal: 10, ref: '0–21 day-strain scale' },
  sleep_hours: { label: 'Sleep', higherIsBetter: true, normal: 7, ref: '7–9 h recommended' },
  sleep_performance: { label: 'Sleep performance', higherIsBetter: true, normal: 85, ref: '≥ 85% of sleep need' },
  steps: { label: 'Steps', higherIsBetter: true, normal: 7000, ref: '~7,000–10,000/day' },
  weight: { label: 'Weight', higherIsBetter: null, normal: 0, ref: 'Tracked over time' },
  systolic_bp: { label: 'Systolic BP', higherIsBetter: false, normal: 120, ref: '< 120 mmHg (normal)' },
  diastolic_bp: { label: 'Diastolic BP', higherIsBetter: false, normal: 80, ref: '< 80 mmHg (normal)' },
  blood_glucose: { label: 'Glucose', higherIsBetter: false, normal: 100, ref: 'Fasting < 100 mg/dL' },
}

const SOURCE_LABELS: Record<string, string> = {
  whoop: 'WHOOP', withings: 'Withings', google_health: 'Google Health', apple_health: 'Apple Health',
  health_connect: 'Health Connect', fhir: 'Records', manual: 'Manual',
}
export function sourceLabel(s: string): string { return SOURCE_LABELS[s] || s }

function dayKey(iso: string): string {
  return (Date.parse(iso) ? new Date(iso) : new Date()).toISOString().slice(0, 10)
}
function shortLabel(key: string, isToday: boolean, isYesterday: boolean): string {
  if (isToday) return 'Today'
  if (isYesterday) return 'Yest'
  const d = new Date(key + 'T00:00:00Z')
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

/** Group by metric type, bucket each type's readings by day (averaged), keep the last 14 days,
 * and return only the types with ≥2 distinct days — i.e. actually chart-worthy. */
export function buildLiveTrends(metrics: LiveMetric[]): LiveTrend[] {
  const byType = new Map<string, LiveMetric[]>()
  for (const m of metrics) {
    if (!byType.has(m.type)) byType.set(m.type, [])
    byType.get(m.type)!.push(m)
  }

  const todayKey = dayKey(new Date().toISOString())
  const yesterdayKey = dayKey(new Date(Date.now() - 86400000).toISOString())
  const out: LiveTrend[] = []

  for (const [type, rows] of byType) {
    const byDay = new Map<string, number[]>()
    for (const r of rows) {
      const k = dayKey(r.recorded_at)
      if (!byDay.has(k)) byDay.set(k, [])
      byDay.get(k)!.push(r.value)
    }
    const days = [...byDay.keys()].sort().slice(-14)
    if (days.length < 2) continue // not chart-worthy yet — stays a plain snapshot tile

    const series = days.map((k) => {
      const vals = byDay.get(k)!
      return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100
    })
    const labels = days.map((k) => shortLabel(k, k === todayKey, k === yesterdayKey))

    const half = Math.max(1, Math.floor(series.length / 2))
    const firstAvg = avg(series.slice(0, half))
    const secondAvg = avg(series.slice(-half))
    const meta = META[type] || { label: prettify(type), higherIsBetter: null, normal: series[series.length - 1], ref: 'Tracked over time' }
    const dir: 'up' | 'down' = secondAvg >= firstAvg ? 'up' : 'down'
    const concern = meta.higherIsBetter === null ? false
      : meta.higherIsBetter ? secondAvg < firstAvg : secondAvg > firstAvg

    const latest = rows[rows.length - 1]
    out.push({
      type, metric: meta.label, unit: latest.unit, source: sourceLabel(latest.source),
      series, labels, dir, concern,
      normal: meta.higherIsBetter === null ? series[series.length - 1] : meta.normal,
      ref: meta.ref,
      note: concern
        ? `Trending ${dir === 'up' ? 'up' : 'down'} over the last ${series.length} days — SARA is watching this.`
        : `Holding steady over the last ${series.length} days.`,
    })
  }
  return out
}

function avg(xs: number[]): number { return xs.reduce((a, b) => a + b, 0) / xs.length }
function prettify(t: string): string { return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) }
